export const accountExperience = ref(null)
export const businessExperiences = ref(new Map())
export const accountExperienceCacheRevision = ref(0)

let cacheOwnerKey = null
let loadPromise = null
let generation = 0

export function buildAccountExperienceOwnerKey(profile) {
  if (!profile || typeof profile !== 'object') return null

  const accountId =
    profile.accountId ??
    profile.account_id ??
    profile.account?.id ??
    ''
  const staffId =
    profile.id ??
    profile.staffId ??
    profile.staff_id ??
    profile.accountStaffId ??
    ''
  const email = String(profile.email || '').trim().toLowerCase()

  if (accountId === '' && staffId === '' && email === '') return null
  return `${String(accountId)}:${String(staffId)}:${email}`
}

/**
 * Invalidate both the result and any request already in flight. The
 * generation guard prevents a slow response from the previous login from
 * writing into the next user's navigation state.
 */
export function resetAccountExperienceCache(nextOwnerKey = null) {
  generation += 1
  cacheOwnerKey = nextOwnerKey || null
  loadPromise = null
  accountExperience.value = null
  businessExperiences.value = new Map()
  accountExperienceCacheRevision.value += 1
}

export function ensureAccountExperienceOwner(ownerKey) {
  const normalizedOwner = ownerKey || null
  if (cacheOwnerKey !== normalizedOwner) {
    resetAccountExperienceCache(normalizedOwner)
  }
}

function applyBusinessList(data) {
  const list = Array.isArray(data?.businesses) ? data.businesses : []
  const map = new Map()

  for (const business of list) {
    const businessId = Number(business.id)
    if (!Number.isFinite(businessId)) continue
    map.set(
      businessId,
      business.experience === 'retail' ? 'retail' : 'hospitality'
    )
  }

  businessExperiences.value = map
  if (map.size === 0) {
    accountExperience.value = null
    return
  }

  const kinds = new Set(map.values())
  accountExperience.value = kinds.size > 1 ? 'mixed' : [...kinds][0]
}

export function loadAccountExperience(ownerKey, loader) {
  const normalizedOwner = ownerKey || null
  ensureAccountExperienceOwner(normalizedOwner)
  if (!normalizedOwner) return Promise.resolve()
  if (loadPromise) return loadPromise

  const requestGeneration = generation
  const requestOwner = normalizedOwner
  let failed = false

  const request = (async () => {
    try {
      const data = await loader()
      if (generation !== requestGeneration || cacheOwnerKey !== requestOwner) return
      applyBusinessList(data)
    } catch (error) {
      failed = true
      if (generation !== requestGeneration || cacheOwnerKey !== requestOwner) return

      // Navigation gating is cosmetic. Fail open and allow a later caller or
      // explicit refresh to retry the transient request.
      accountExperience.value = null
      businessExperiences.value = new Map()
      console.warn(
        '[useAccountExperience] load failed (defaulting to full nav):',
        error?.message || error
      )
    }
  })()

  loadPromise = request
  void request.finally(() => {
    if (
      failed &&
      generation === requestGeneration &&
      cacheOwnerKey === requestOwner &&
      loadPromise === request
    ) {
      loadPromise = null
    }
  })

  return request
}
